import { Optional, Component } from '@angular/core';
import { OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { ElementRef, HostBinding, HostListener } from '@angular/core';
import { Input, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ContentChildren, QueryList } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor }  from "@angular/forms";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { viewService } from '@unpispas/upp-base';

/*************************************/
/* UPP-SELECT-OPTION COMPONENT       */
/*************************************/

/**
 * Represents an option within the `upp-select` component.
 */
@Component({
    selector: 'upp-select-option',
    templateUrl: './parts/upp-option.html',
    styleUrls: ['./upp-select.scss'],
})
export class UppSelectOptionComponent {
    /** The value associated with this option. */    
    @Input() value: any = null;

    /** Event emitted when the option is selected. */
    @Output() Select = new EventEmitter <any> ();

    /**
     * Emits the `Select` event with the current value when selected.
     */    
    OnSelect(ev?: Event): void {
        ev?.stopPropagation();
        ev?.preventDefault();

        this.Select.emit(this.value);
    }
}

/*************************************/
/* UPP-SELECT-LIST COMPONENT         */
/*************************************/

/**
 * Represents a list of `upp-select-option` components.
 */
@Component({
    selector: 'upp-select-list',
    templateUrl: './parts/upp-list.html',
    styleUrls: ['./upp-select.scss'],
})
export class UppSelectListComponent implements AfterViewInit, OnDestroy {
    private _option_subscription: Subscription | null = null;
    private _option_subscriptions: Subscription[] = [];

    constructor(
        @Optional() private hostRef: ElementRef<HTMLElement> | null = null,
        @Optional() private change: ChangeDetectorRef | null = null,
    ) {
        // nothing to do
    }

    /**
     * Lifecycle hook that unsubscribes from subscriptions when the component is destroyed.
     */    
    ngOnDestroy(): void {
        if (this._option_subscription){
            this._option_subscription.unsubscribe();
            this._option_subscription = null;
        }

        for (const _subscription of this._option_subscriptions){
            _subscription.unsubscribe();
        }
        this._option_subscriptions = [];
    }

    /** Event emitted when an option is selected from the list. */
    @Output() Select = new EventEmitter <any> ();

    private OnSelect(value: any){
        this.Select.emit(value);
    }

    /** Query list of `upp-select-option` components within this list. */
    @ContentChildren(UppSelectOptionComponent) options!: QueryList<UppSelectOptionComponent>;

    /**
     * Lifecycle hook that sets up option listeners after the view initializes.
     */    
    ngAfterViewInit() {
        this._OptionListeners();

        if (this._option_subscription){
            this._option_subscription.unsubscribe();
            this._option_subscription = null;
        }

        this._option_subscription = this.options.changes.subscribe(() => {
            this._OptionListeners();
        });
    }

    private _OptionListeners() {
        for (const _subscription of this._option_subscriptions){
            _subscription.unsubscribe();
        }
        this._option_subscriptions = [];

        this.options.forEach(option => {
            const _subscription = option.Select.subscribe(value => {
                this.OnSelect(value);
            });
            this._option_subscriptions.push(_subscription);
        });
    }   
    
    private _expand = false;

    /**
     * Whether the list is expanded.
     */    
    get IsExpand(): boolean {
        return this._expand;
    }

    set IsExpand(value: boolean){
        if (!value && this._expand) {
            this._ReleaseFocus();
        }

        this._expand = value;
    }

    private _ReleaseFocus(): void {
        if (typeof document === 'undefined') {
            return;
        }
        
        const active = document.activeElement;
        const host = this.hostRef?.nativeElement;
        if (active instanceof HTMLElement && host?.contains(active)) {
            active.blur();
        }
    }

    /**
     * Expands or collapses the list.
     */    
    Expand(value: boolean){
        this.IsExpand = value;
        this.change?.markForCheck();
    }
}

/*************************************/
/* UPP-SELECT-ITEM COMPONENT         */
/*************************************/

/**
 * Represents an item in the `upp-select` component.
 */
@Component({
    selector: 'upp-select-item',
    templateUrl: './parts/upp-item.html',
    styleUrls: ['./upp-select.scss'],
})
export class UppSelectItemComponent {
    /** Event emitted when the item is expanded. */
    @Output() Expand = new EventEmitter <boolean> ();

    private _expand = false;

    /**
     * Whether the item is expanded.
     */    
    get IsExpand(): boolean {
        return this._expand;
    }

    set IsExpand(value: boolean) {
        this._expand = value;
    }

    OnExpand(value: boolean){
        if (this.IsExpand != value){
            this.IsExpand = value;
            this.Expand.emit(this.IsExpand);    
        }
    }

    OnItemBoxClick(): void {
        this.OnExpand(!this.IsExpand);
    }

    OnItemBoxToggle(ev: Event): void {
        ev.preventDefault();

        this.OnExpand(!this.IsExpand);
    }
}

/*************************************/
/* UPP-SELECT COMPONENT              */
/*************************************/

/**
 * The main `upp-select` component.
 * Implements `ControlValueAccessor` for form control integration.
 */
@Component({
    selector: 'upp-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./upp-select.html',
    styleUrls: [ 
        './upp-select.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppSelectComponent),
            multi: true
        }            
    ]    
})
export class UppSelectComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges, OnDestroy {
    /** The name of the form control this input is bound to. */
    @Input() formControlName = '';
    /** Disables the input when set to true. */
    @Input() disabled = false;
    /** When non-empty, floating caption above the trigger (same treatment as `upp-input` title). */
    @Input() title = '';

    @HostBinding('class.has-title')
    get hasTitle(): boolean {
        return !!this.title;
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<any>(); 
    /** Emits an event when the input gains focus. */
    @Output() Focus = new EventEmitter<void>(); 
    
    /** optional FromGroup */
    public formGroup: FormGroup | null = null
    public formControl: AbstractControl | null = null;

    private _innerValue: any = null;  

    /* eslint-disable @typescript-eslint/no-unused-vars */
    /** Callback to notify Angular Forms when the value changes. */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    /** Callback to notify Angular Forms when the input is touched. */
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /**
     * Constructor for dependency injection.
     * @param controlContainer Provides access to the parent form group.
     * @param change A service to trigger manual change detection.
     * @param hostRef Host element of `upp-select` (used for outside-click dismiss).
     * @param view View mode source for dropdown height (mobile vs desktop).
     */    
    constructor(
        @Optional() private controlContainer: ControlContainer,
        private change: ChangeDetectorRef,
        private hostRef: ElementRef<HTMLElement>,
        public view: viewService,
    ) {
        // nothing to do
    }

    private _form_subscription: Subscription | null = null;
    private _list_subscription: Subscription | null = null;
    private _item_subscription: Subscription | null = null;
    private _expand_subscription: Subscription[] = [];
    private _list_subscriptions: Subscription[] = [];

    @HostListener('document:click', ['$event'])
    private _onDocumentClick(ev: MouseEvent): void {
        if (typeof document === 'undefined') {
            return;
        }
        if (!this.items?.some(item => item.IsExpand)) {
            return;
        }
        const host = this.hostRef.nativeElement;
        const path = typeof ev.composedPath === 'function' ? ev.composedPath() : [];
        if (path.includes(host)) {
            return;
        }
        const t = ev.target;
        if (t && host.contains(t as Node)) {
            return;
        }
        this.items.forEach(item => {
            item.OnExpand(false);
        });
        this.change.markForCheck();
    }

    @HostListener('document:keydown', ['$event'])
    private _onDocumentKeydown(ev: KeyboardEvent): void {
        if (ev.key !== 'Escape') {
            return;
        }
        if (!this.items?.some(item => item.IsExpand)) {
            return;
        }
        ev.preventDefault();
        this.items.forEach(item => {
            item.OnExpand(false);
        });
        this.change.markForCheck();
    }

    /**
     * Lifecycle hook that initializes the component.
     * Sets up form control integration and initializes the value.
     */    
    ngOnInit() {
        this._innerValue = this.value;
        
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this.value = this.formControl.value || null;

            this._form_subscription = this.formControl.valueChanges.subscribe(
            () => {
                this.value = this.formControl?.value || null;
            });
        }

        this.onChange();
    }

    /** Query list of child `upp-select-item` components. */
    @ContentChildren(UppSelectItemComponent) items!: QueryList<UppSelectItemComponent>;

    /** Query list of child `upp-select-list` components. */
    @ContentChildren(UppSelectListComponent) lists!: QueryList<UppSelectListComponent>;

    /**
     * Lifecycle hook that runs after the component’s view has been initialized.
     * It sets up listeners for child components.
     */    
    ngAfterViewInit() {
        this._ItemListeners();
        this._ListListeners();

        if (this._item_subscription){
            this._item_subscription.unsubscribe();
            this._item_subscription = null;
        }

        if (this._list_subscription){
            this._list_subscription.unsubscribe();
            this._list_subscription = null;
        }

        this._item_subscription = this.items.changes.subscribe(() => {
            this._ItemListeners();
        });

        this._list_subscription = this.lists.changes.subscribe(() => {
            this._ListListeners();
        });
    }

    /**
     * Sets up event listeners for `upp-select-item` components.
     * Expands or collapses the corresponding lists when an item is expanded.
     */    
    private _ItemListeners() {
        for(const _subscription of this._expand_subscription){
            _subscription.unsubscribe();
        }
        this._expand_subscription = [];

        this.items.forEach(item => {
            const _subscription = item.Expand.subscribe(value => {
                for (const list of this.lists){
                    list.Expand(value);
                }
                this.change.markForCheck();
            });

            this._expand_subscription.push(_subscription);
        });
    }

    /**
     * Sets up event listeners for `upp-select-list` components.
     * Updates the selected value when a list item is selected.
     */    
    private _ListListeners() {
        for(const _subscription of this._list_subscriptions){
            _subscription.unsubscribe();
        }
        this._list_subscriptions = [];

        this.lists.forEach(list => {
            const _subscription = list.Select.subscribe(value => {
                this._CollapsePanels();
                if (value !== this._innerValue) {
                    this.value = value;
                }
                else {
                    this.change.markForCheck();
                }
            });

            this._list_subscriptions.push(_subscription);
        });
    }

    /**
     * Closes every trigger and list panel; list state is updated directly so item/list cannot stay out of sync.
     */
    private _CollapsePanels(): void {
        this.items?.forEach(item => {
            item.OnExpand(false);
        });
        
        this.lists?.forEach(list => {
            list.Expand(false);
        });
    }

    /**
     * Lifecycle hook that detects changes to the input's properties.
     */    
    ngOnChanges() {
        this.onChange();
    }
    
    /**
     * Lifecycle hook that cleans up resources on component destruction.
     * Unsubscribes from form control value changes.
     */
    ngOnDestroy() {
        if (this._form_subscription){
            this._form_subscription.unsubscribe();
            this._form_subscription = null;
        }  
        
        if (this._item_subscription){
            this._item_subscription.unsubscribe();
            this._item_subscription = null;
        }

        if (this._list_subscription){
            this._list_subscription.unsubscribe();
            this._list_subscription = null;
        }

        for(const _subscription of this._expand_subscription){
            _subscription.unsubscribe();
        }
        this._expand_subscription = [];

        for(const _subscription of this._list_subscriptions){
            _subscription.unsubscribe();
        }
        this._list_subscriptions = [];
    }

    /**
     * Handles value change events and triggers UI updates.
     */    
    onChange(): void {
        this.Changed.emit(this._innerValue);
        this._CollapsePanels();
        this.change.markForCheck();
    }

    /**
     * Emits a focus event when the input gains focus.
     */    
    onFocus(): void {
        this.Focus.emit();
    }

    /********************************************/
    /* disable control                          */
    /********************************************/

    /**
     * Determines whether the input is disabled.
     * @returns {boolean} True if the input is disabled; otherwise, false.
     */    
    get _disabled(): boolean {
        // if directly disabled, return true
        if (this.disabled){
            return true;
        }

        // check if the the form control is disabled
        const _formcontrol = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (_formcontrol){
            return _formcontrol.disabled;
        }

        // not disabled
        return false;
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    @Input()

    /**
     * Gets the current value of the input.
     * @returns {string} The current value of the input.
     */    
    get value(): string {
        return this._innerValue;
    }

    /**
     * Sets a new value for the input.
     * Updates the internal value, notifies Angular Forms, and triggers value change events.
     * @param {string} v - The new value to set.
     */    
    set value(v: string) {
        if (v !== this._innerValue) {
            this._innerValue = v;

            this.onChange();

            this._onChangeCallback(v);
            this._onTouchCallback();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the input. Required by `ControlValueAccessor`.
     * @param {string} value - The value to write.
     */    
    writeValue(value: any) {
        if (value !== this._innerValue) {
            this._innerValue = value;
            this.change.markForCheck();
        }
    }

    /**
     * Registers a callback function to be called when the input value changes.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }    
}

